import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  TouchableOpacity
} from 'react-native'

import { useRouter } from 'expo-router'

import { DS } from '../../design-system'
import { useStore } from '../../models'
import CancellationSurveyStep1 from './components/CancellationSurveyStep1'
import CancellationSurveyStep2 from './components/CancellationSurveyStep2'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'

const MembershipCancellationSurveyScreen: React.FC = observer(() => {
  const router = useRouter()
  const {
    game: { setToast },
    memberships: { subscription, cancelMembership, getMySubscription, isApple },
    analytics: { setEvent, setUserIdentify },
    session: { user }
  } = useStore()
  const scrollRef = useRef<ScrollView>(null)
  const [reason, setReason] = useState('')
  const [feedback, setFeedback] = useState<string | undefined>(undefined)
  const [inputFocus, setInputFocus] = useState(false)
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(1)
  useSubscriptionGuard()
  const handleFocus = () => {
    if (scrollRef.current) {
      setInputFocus(true)
      setTimeout(() => {
        scrollRef.current?.scrollToEnd({ animated: true })
      }, 250)
    }
  }

  const cancelMembershipHandler = async () => {
    try {
      setLoading(true)
      await cancelMembership(subscription?.stripeSubscriptionId ?? '', {
        membershipTypeId: subscription?.membershipTypeId ?? '',
        reason: reason ?? '',
        feedback: feedback ?? undefined
      })

      await getMySubscription()
      if (subscription) {
        setEvent('Membership Cancelled', {
          product: subscription.type
        })
        setEvent('Membership Cancellation Survey', {
          cancel_reason_option: reason,
          cancel_reason_response: feedback
        })
        user?._id &&
          setUserIdentify(user._id, {
            membership_type: 'free_user'
          })
      }

      setToast({
        kind: 'success',
        message: 'Membership updated'
      })
      if (isApple) {
        return setStep(2)
      }

      router.navigate('/lobby/membership')
    } catch (e) {
      Alert.alert('Error', 'There was an error cancelling your membership')
    } finally {
      setLoading(false)
    }
  }

  const goBack = () => {
    if (step === 1 && router.canGoBack()) {
      subscription &&
        setEvent('Membership Cancel Exited', {
          from_screen: 'Survey',
          tier: subscription.type
        })
      router.back()
      return
    }
    router.navigate('/lobby/membership')
  }

  return (
    <DS.Frame style={styles.wrapperContainer}>
      <DS.Frame style={styles.headerDivider} />
      <SafeAreaView style={styles.safeAreaContainer}>
        <DS.Frame style={styles.containerHeader}>
          <DS.Frame style={styles.headerContainer}>
            <DS.Text variant="Body Medium/Medium">Cancel Membership</DS.Text>
            <TouchableOpacity style={styles.returnButton} onPress={() => goBack()}>
              <DS.Icon name="Arrow---left" size={20} color="Global/Black" />
            </TouchableOpacity>
          </DS.Frame>
        </DS.Frame>
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 60 : 0}
        >
          <ScrollView ref={scrollRef}>
            {step === 1 ? (
              <CancellationSurveyStep1
                handleFocus={handleFocus}
                setInputFocus={setInputFocus}
                inputFocus={inputFocus}
                setFeedback={setFeedback}
                setReason={setReason}
                reason={reason}
              />
            ) : null}
            {step === 2 ? <CancellationSurveyStep2 /> : null}
          </ScrollView>
        </KeyboardAvoidingView>

        {step === 1 && (
          <DS.Frame style={styles.buttonContainer}>
            <DS.Button
              variant="Large/Primary"
              type="On Light"
              disabled={reason.length === 0}
              onPress={cancelMembershipHandler}
            >
              Submit
            </DS.Button>

            {loading && (
              <DS.Frame style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="white" />
              </DS.Frame>
            )}
          </DS.Frame>
        )}
      </SafeAreaView>
    </DS.Frame>
  )
})

const styles = StyleSheet.create({
  wrapperContainer: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  safeAreaContainer: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    borderTopRightRadius: 24,
    borderTopLeftRadius: 24,
    gap: 42,
    transform: [{ translateY: -24 }]
  },
  headerNumber: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginVertical: 8,
    gap: 8
  },
  containerHeader: {
    padding: 24,
    paddingBottom: 0,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: DS.palette['Brand/Cream'],
    position: 'relative',
    borderTopRightRadius: 24,
    borderTopLeftRadius: 24
  },
  headerContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  surveyContainer: {
    flexGrow: 1,
    backgroundColor: `${DS.palette['Global/White']}80`,
    padding: 24,
    marginHorizontal: 24,
    borderRadius: 12
  },
  title: {
    textAlign: 'center',
    paddingBottom: 12
  },
  checkButton: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    gap: 12
  },
  checkContainer: {
    gap: 4
  },

  checkButtonContainer: {
    backgroundColor: DS.palette['Global/White'],
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 12
  },
  optionFrame: {
    flex: 1,
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  returnButton: {
    padding: 8,
    borderRadius: 16,
    position: 'absolute',
    width: 40,
    height: 40,
    top: -8,
    left: 0
  },
  divider: {
    height: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    width: '100%'
  },
  buttonContainer: {
    position: 'relative',
    marginTop: 'auto',
    width: '100%',
    marginBottom: 16,
    paddingHorizontal: 24
  },
  headerDivider: {
    height: 90,
    backgroundColor: DS.palette['Global/Black'],
    width: '100%'
  },
  inputContainer: {
    backgroundColor: DS.palette['Global/White'],
    justifyContent: 'flex-end',
    paddingLeft: 32
  },
  input: {
    height: 41,
    borderColor: DS.palette['Global/Black'],
    borderWidth: 1,
    borderRadius: 8
  },
  keyboardAvoidingView: {
    flex: 1
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 130,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center'
  }
})

export default MembershipCancellationSurveyScreen
